import { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Info, StickyNote, EyeOff, Eye, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { mapsUrl } from "@/components/MapLink";

const getGeo = () => new Promise((resolve) => {
  if (!navigator.geolocation) { resolve(null); return; }
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
    () => resolve(null), { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 });
});

const fmt = (ts) => new Date(ts).toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export function JobInfoCard({ jobId, info, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(info || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(info || ""); }, [info]);
  const save = async () => {
    setBusy(true);
    try { await client.put(`/jobs/${jobId}/info`, { info: val }); toast.success("Job information saved for every visit"); setEditing(false); onSaved && onSaved(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="jp-section-info">
      <div className="flex items-center justify-between mb-2">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-amber-800"><Info className="h-3.5 w-3.5" />Job information</p>
        {canEdit && !editing && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(true)} data-testid="jp-info-edit">Edit</Button>}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea value={val} onChange={(e) => setVal(e.target.value)} rows={3} maxLength={2000} placeholder="e.g. Entrance round the back, key code 1234" data-testid="jp-info-input" className="bg-white" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setVal(info || ""); }}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark" data-testid="jp-info-save">{busy && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Save</Button>
          </div>
        </div>
      ) : <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="jp-info-text">{info || <span className="text-slate-400">No information added yet.</span>}</p>}
    </div>
  );
}

const NoteItem = ({ n, canHide, onToggle }) => (
  <div className={`rounded-lg border p-3 ${n.hidden ? "border-slate-300 bg-slate-50" : "border-slate-200 bg-white"}`} data-testid={`job-note-${n.id}`}>
    <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</p>
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
      <span className="font-semibold text-slate-700">{n.byName}</span>
      <span>{fmt(n.at)}</span>
      {n.visitDate && <span>Visit {n.visitDate}</span>}
      {n.geo ? <a href={mapsUrl(null, n.geo)} target="_blank" rel="noreferrer" className="text-brand hover:underline inline-flex items-center gap-0.5" data-testid={`job-note-geo-${n.id}`}><MapPin className="h-3 w-3" />Waypoint</a> : <span>No location</span>}
      {n.hidden && <Badge variant="outline" className="text-[10px] border-slate-400">Hidden from cleaners</Badge>}
      {canHide && (
        <button type="button" onClick={() => onToggle(n)} className="ml-auto inline-flex items-center gap-1 text-slate-500 hover:text-slate-900" data-testid={`job-note-hide-${n.id}`}>
          {n.hidden ? <><Eye className="h-3.5 w-3.5" />Show to cleaners</> : <><EyeOff className="h-3.5 w-3.5" />Hide from cleaners</>}
        </button>
      )}
    </div>
  </div>
);

export function JobNotesLog({ jobId }) {
  const [notes, setNotes] = useState([]);
  const [canHide, setCanHide] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    client.get(`/jobs/${jobId}/notes`).then(({ data }) => { setNotes(data.notes); setCanHide(data.canHide); }).catch(() => {});
  }, [jobId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    setBusy(true);
    try { const geo = await getGeo(); await client.post(`/jobs/${jobId}/notes`, { text, geo }); setText(""); toast.success("Note added"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const toggle = async (n) => {
    try { await client.patch(`/jobs/notes/${n.id}`, { hidden: !n.hidden }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="jp-section-job-notes">
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><StickyNote className="h-3.5 w-3.5 text-brand" />Job notes ({notes.length})</p>
      <div className="space-y-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={2000} placeholder="Add a note, e.g. there were no keys in the key box" data-testid="job-note-input" />
        <div className="flex justify-end">
          <Button size="sm" onClick={add} disabled={busy || !text.trim()} className="bg-brand hover:bg-brand-dark" data-testid="job-note-add">{busy && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Add note</Button>
        </div>
      </div>
      {notes.length === 0 ? <p className="text-sm text-slate-400">No notes yet.</p> : notes.map((n) => <NoteItem key={n.id} n={n} canHide={canHide} onToggle={toggle} />)}
    </div>
  );
}
